import { IPublicTypeRootSchema } from "@alilc/lowcode-types";
import React, { useEffect, useState } from "react";
import ReactRenderer from "@yuntoo/page-react-renderer";
import { LowcodeBreadCrumb } from "@/components";
import { ytFetch } from "@/services";
import { registerSdk } from "@/utils";
import { getAppInfo } from "@/services";
import { AssetLoader } from "./utils";
// import assets from "./assets-mock";
export interface PageData {
  pageSchema?: IPublicTypeRootSchema;
  components?: Record<string, any>;
}

export interface IAssets {
  packages?: {
    package: string;
    urls: string[];
  }[];
  components?: {
    exportName: string;
    urls: string[];
  }[];
}

const RenderLowcodePage = (props) => {
  const {
    schema,
    nodePermitList = [],
    pageId = "",
    location,
    navigate,
    smartPageHistory,
    onePageInfo,
  } = props;
  const { innerPagePath: onePagePath, innerSearchParams = {} } =
    onePageInfo || {};
  const [pageData, setPageData] = useState<PageData>({});
  const [sdkClient, setSdkClient] = useState(null);
  // 将 nodePermitList 转换为 map 结构
  const nodePermitMap = new Map(
    nodePermitList.map(({ resourceCode, allowedActions }) => [
      resourceCode,
      allowedActions.includes("0") ? false : true,
    ]),
  );

  // 判断节点 node 是否可访问
  const isNodeAccessible = (resourceCode: string) => {
    if (!resourceCode) return true;
    return nodePermitMap.get(resourceCode) === false ? false : true;
  };

  // 初始化低码引擎渲染数据
  const initAlc = async () => {
    // 0. 从appInfo中获取当前app的assets配置, 全局只需要执行一次即可
    const appInfo = await getAppInfo();
    const { extend } = appInfo;
    let assets: IAssets = {};
    try {
      const { assets: appAssets } = extend;
      if (typeof appAssets === "string") {
        assets = JSON.parse(appAssets);
      } else if (typeof appAssets === "object") {
        assets = appAssets || {}; // 防止appAssets默认为null
      }
    } catch (error) {
      console.warn("error", error);
    }

    // 1. 这里拿到引擎需要的低码页面schema结构
    const pageSchema = schema?.componentsTree?.[0];
    if (!pageSchema) return;

    // 2. 从assets配置中导出关键的packages和components这两个结构
    // packages 需要加载其中的所有url资源, components指定低码组件包, 需要注册给渲染引擎
    const { packages = [], components = [] } = assets;

    // 3. 处理需要动态加载的资源
    // 这里暂时用引擎提供的方法，后续可以自己封装掉，本质就是动态load加载css和js（需判断是否已加载过）
    const libraryAsset = [];
    // 处理packages资源, 如果window上没有这个包，才需要加载
    packages.forEach(({ package: packageName, urls }) => {
      if (!window[packageName] && urls?.length) {
        libraryAsset.push(urls);
      }
    });
    // 处理components资源, 如果window上没有这个组件，才需要加载
    components.forEach(({ exportName, urls }) => {
      if (!window[exportName] && urls?.length) {
        libraryAsset.push(urls);
      }
    });
    // 动态加载packages中的资源
    if (libraryAsset.length > 0) {
      const assetLoader = new AssetLoader();
      await assetLoader.load(libraryAsset);
    }

    // 4. 处理低码组件注册，这里和官方方法不同，直接把assets中componets指定的所有组件都注册到lowcodeComponents上
    // 如果有多个低码组件包中的子组件重名，则后面的覆盖前面的
    let lowcodeComponents = {
      ...window.YUNTOO_META,
      ...window.YUNTOO_SMART,
    }; // 这些是yuntoo默认组件
    components.forEach(({ exportName }) => {
      if (window[exportName]) {
        lowcodeComponents = { ...lowcodeComponents, ...window[exportName] };
      }
    });

    // 5. setState
    setPageData({ pageSchema, components: lowcodeComponents });
  };

  // 初始化sdk
  const initSdk = async () => {
    const client = await registerSdk;
    setSdkClient(client);
  };

  useEffect(() => {
    initAlc();
    initSdk();

    // TODO: 打印 debug 信息, 方便调试
    window.YUNTOO_RENDER = {
      schema,
      pageId,
    };
  }, [schema, pageId]);

  if (!pageData.pageSchema || !sdkClient) return <></>;

  return (
    <ReactRenderer
      schema={pageData.pageSchema}
      components={pageData.components}
      appHelper={{
        // onePage页面情况下, searchParams 不直接在url中出现, 需要显示传递指定urlParams, 供ALC页面消费
        ...(onePagePath && Object.keys(innerSearchParams).length
          ? { urlParams: innerSearchParams }
          : {}),
        yt: {
          topLeft: onePagePath ? null : <LowcodeBreadCrumb />, // 注入面包屑, onePage 模式不注入
          smartPageHistory,
          theme: { inherit: true }, // 完全继承容器应用的theme即可
          nodePermitMap,
        },
        utils: {
          sdkClient, // sdk client 实例
          ytFetch, // 运行态 ytFetch 实例
          isNodeAccessible, // 判断节点是否可访问
        },
        location,
        history: navigate,
      }}
      customCreateElement={(Component, props, children) => {
        // 统一处理组件级别的 _resourceCode 权限判断
        if (!isNodeAccessible(props._resourceCode)) {
          return null;
        }
        return <Component {...props} children={children} />;
      }}
    />
  );
};

export default RenderLowcodePage;
